//@ts-check
import React, {Component} from 'react';
import {arrayOf, bool, string, object} from 'prop-types';
import classNames from 'classnames';
import {
    always,
    applySpec,
    chain,
    fromPairs,
    indexBy,
    keys,
    map,
    pipe,
    prop,
    propEq,
    toPairs,
    values,
    uniq,
    reject,
    append,
    whereEq,
    tap,
} from 'ramda';
import {default as TextInput} from '@sinclair-digital/sbg-storyline-components/src/components/forms/TextInput';
import {default as Button} from '@sinclair-digital/sbg-storyline-components/src/components/Button';
import {default as Checkbox} from '@sinclair-digital/sbg-storyline-components/src/components/forms/Checkbox';
import {messageHandler} from '@sinclair-digital/stl-common/src/sinclair/sinclair.uiTools';
import {moduleApi, moduleShape as activeModuleShape} from '@sinclair-digital/sbg-storyline-sdk/lib/storyline/shapes';
import Module from '@sinclair-digital/sbg-storyline-sdk/lib/storyline/Module';
import {Pure} from '@sinclair-digital/sbg-util/dist/es/react';
import {getFeatureFlag} from '@sinclair-digital/sbg-storyline-sdk/lib/storyline/AppContext';

import modules, {
    getPluginRegistry,
    getPluginData,
    hideModule,
    listenForModuleUpdates,
    loadPlugins,
    removeListener,
    removeModule,
    showModule,
} from '../../modules';
import {PluginManager} from './PluginManager';

import styles from './ModuleManager.scss';

const ModuleList = Pure('ModuleList', {
    propTypes: {
        modList: arrayOf(activeModuleShape),
    },
})(({modList}) => (
    <div className={styles.ModuleList}>
        <h1>Active Modules:</h1>
        <ul>{modList && modList.map(({id, title}) => <li key={id}>{title}</li>)}</ul>
    </div>
));

export const getStoredFeatureFlags = () => {
    try {
        return JSON.parse(localStorage.getItem('flags')) || {};
    } catch (e) {
        return {};
    }
};

const getOverrides = () => {
    const overrides = getStoredFeatureFlags();
    return Object.keys(overrides).map(title => ({title, isEnabled: overrides[title], hasOverride: true}));
};

const getFeatures = appContext => {
    const debugState = appContext.getAllFeatureFlags(); // This method lies
    const featureFlags = debugState[debugState.activeThru];
    const overrides = getOverrides();
    return overrides.concat(
        Object.keys(featureFlags)
            .filter(name => !name.endsWith('.On') && !name.endsWith('.Off') && !overrides.some(ff => ff.title === name))
            .map(title => ({
                title,
                isEnabled: appContext.getFeatureFlag(title),
                hasOverride: false,
            })),
    );
};

class FeatureList extends Component {
    static propTypes = {
        editable: bool,
        appContext: object,
    };

    constructor(props) {
        super(props);
        this.state = {
            featureFlag: '',
            features: getFeatures(props.appContext),
        };
    }

    onFeatureFlagChange = ({target: {value}}) => {
        this.setState({featureFlag: value});
    };

    addOverride = () => {
        if (!this.state.features.some(propEq('title', this.state.featureFlag))) {
            const flags = getStoredFeatureFlags();
            flags[this.state.featureFlag] = true;
            localStorage.setItem('flags', JSON.stringify(flags));
            this.setState(({features}) => ({
                features: [{title: this.state.featureFlag, isEnabled: true, hasOverride: true}].concat(features),
                featureFlag: '',
            }));
        }
    };

    onToggleFeature = title => () => {
        const val = !this.props.appContext.getFeatureFlag(title);
        this.props.appContext.setFeatureFlag(title, val);
        const features = this.state.features.map(ff =>
            ff.title === title
                ? {
                    ...ff,
                    isEnabled: val,
                    hasOverride: true,
                }
                : ff,
        );
        this.setState({features});
    };

    onDeleteOverrides = () => {
        localStorage.removeItem('flags');
        this.setState({features: getFeatures(this.props.appContext)});
        window.location.reload(); // Prevents cache desync with debugModel.
    };

    onFeatureFlagKeyPress = ({key}) => {
        if (key === 'Enter') {
            this.addOverride();
        }
    };

    render() {
        const {editable} = this.props;
        const {features} = this.state;
        return (
            <div className={styles.FeatureList}>
                <h1>Feature Flags:</h1>
                <div className={styles.customOverride}>
                    <TextInput
                        disabled={!editable}
                        value={this.state.featureFlag}
                        placeholder="Feature Flag Name"
                        onChange={this.onFeatureFlagChange}
                        name="featureFlagOverride"
                        onKeyPress={this.onFeatureFlagKeyPress}
                    />
                    <Button onClick={this.addOverride} disabled={!editable}>
                        Custom Override
                    </Button>
                </div>
                <p>
                    * has local override {' '}
                    <Button onClick={this.onDeleteOverrides} disabled={!editable}>
                        delete overrides
                    </Button>
                </p>
                <ul className={styles.scrollBox}>
                    {features &&
                        features
                            .sort((a, b) => {
                                const aTitle = a.title, bTitle = b.title;
                                return aTitle.localeCompare(bTitle);
                            })
                            .map(({title, isEnabled, hasOverride}) => (
                                <li key={title}>
                                    <Checkbox
                                        theme="toggle"
                                        label={title + (hasOverride ? '*' : '')}
                                        onChange={this.onToggleFeature(title)}
                                        checked={isEnabled}
                                        disabled={!editable}
                                    />
                                </li>
                            ))
                    }
                </ul>
            </div>
        );
    }
}

const overridePluginVersion = pluginVersionByName => ({current, name, ...plugin}) => ({
    ...plugin,
    name,
    active: pluginVersionByName[name],
    current,
});

export class ModuleManager extends Component {
    static propTypes = {
        className: string,
        ...moduleApi,
    };

    state = {
        pluginRegistry: null,
        pluginModuleMap: {},
    };

    updatePlugins = () => {
        getPluginRegistry().then(pluginRegistry => {
            const pluginModuleMap = pipe(
                values,
                chain(({name, modules: moduleList = []}) =>
                    moduleList.map(applySpec([prop('moduleName'), always(name)])),
                ),
                fromPairs,
            )(pluginRegistry.plugins);

            const overridePlugins = pipe(
                keys,
                map(
                    pipe(
                        getPluginData,
                        applySpec([prop('plugin'), prop('version')]),
                    ),
                ),
                fromPairs,
                overridePluginVersion,
            )(pluginRegistry.plugins);

            this.setState({
                pluginRegistry: {
                    ...pluginRegistry,
                    plugins: map(overridePlugins, pluginRegistry.plugins || {}),
                },
                pluginModuleMap,
            });
        });
    };

    componentDidMount = () => {
        this.updatePlugins();
        listenForModuleUpdates(this.updatePlugins, 'ModuleManager');
    };

    componentWillUnmount = () => {
        removeListener('ModuleManager');
    };

    enablePlugin = plugin => {
        const localPlugins = this.getLocalPlugins();
        localPlugins.push(plugin);
        localStorage.setItem('localPlugins', JSON.stringify(localPlugins));
        return loadPlugins([plugin])
            .then(tap(() => messageHandler('success', 'plugin loaded')))
            .catch(err => {
                messageHandler('error', 'failed to load plugin');
                return Promise.reject(err);
            });
    };

    /**
     * Disable a plugin (?)
     *
     * @param {string|Object} plugin
     * @returns {Promise.<Array>}
     */
    disablePlugin = plugin => {
        if (typeof plugin !== 'string') {
            if (typeof plugin.plugin === 'string') {
                // eslint-disable-next-line no-param-reassign
                plugin = plugin.plugin;
            } else {
                throw new Error(`Unexpected input provided while trying to disable plugin`);
            }
        }
        const pairs = toPairs(this.state.pluginModuleMap);
        const _modules = pairs.filter(([, pluginName]) => {
            return pluginName === plugin;
        });
        return Promise.all(_modules.map(module => removeModule(module)));
    };

    enableModule = ({module}) => {
        return showModule(module);
    };

    disableModule = ({module}) => {
        return hideModule(module);
    };

    /**
     * @type {() => {plugin: string, version: string, url: string}[]}
     */
    getLocalPlugins = () => JSON.parse(localStorage.getItem('localPlugins') || '[]');

    /**
     * @type {(pluginName: string, url: string) => Promise<unknown>}
     */
    addLocalOverride = (pluginName, url) => {
        this.disablePlugin(pluginName);
        const plugin = {plugin: pluginName, version: 'local', source: url};
        const localPlugins = pipe(
            reject(whereEq({plugin: pluginName})),
            append(plugin),
        )(this.getLocalPlugins());
        localStorage.setItem('localPlugins', JSON.stringify(uniq(localPlugins)));
        return loadPlugins([plugin])
            .then(tap(() => messageHandler('success', 'Override activated')))
            .catch(err => {
                messageHandler('error', 'failed to load override');
                return Promise.reject(err);
            });
    };

    removeLocalOverride = (pluginName) => {
        this.disablePlugin(pluginName);
        const localPlugins = reject(whereEq({plugin: pluginName}), this.getLocalPlugins());
        localStorage.setItem('localPlugins', JSON.stringify(uniq(localPlugins)));
        window.location.reload(); // lazy reset
    }

    componentDidCatch(error) {
        this.setState({error});
    }

    render() {
        const classList = classNames(styles.ModuleManager, this.props.className, {});

        const modList = modules.getModules();
        const activeModules = indexBy(prop('moduleName'), modList);
        const pluginRegistry = this.state.pluginRegistry;

        const isAdmin = this.props.appContext.hasUserPermissionSet('user-admin.view');

        return this.state.error ? (
            this.state.error
        ) : (
            <div className={classList}>
                <div>
                    <ModuleList {...{modList}} />
                    <FeatureList
                        {...{
                            editable: isAdmin,
                            appContext: this.props.appContext,
                        }}
                    />
                </div>
                {isAdmin && (
                    <PluginManager
                        {...pluginRegistry}
                        {...{
                            activeModules,
                            pluginModuleMap: this.state.pluginModuleMap,
                            enablePlugin: this.enablePlugin,
                            disablePlugin: this.disablePlugin,
                            enableModule: this.enableModule,
                            disableModule: this.disableModule,
                            addLocalOverride: this.addLocalOverride,
                            removeLocalOverride: this.removeLocalOverride,
                        }}
                    />
                )}
            </div>
        );
    }
}

export default ModuleManager;

// TODO: This should also require a firejack permission that a sales admin should lack - ADM 1.12.17
// requirePageBuilderFlag :: userModel -> Boolean
const requirePageBuilderFlag = getFeatureFlag('pageBuilderModule');

export const ModuleManagerModule = new Module({
    moduleName: 'ModuleManager',
    title: 'Module Manager',
    route: 'admin/modules',
    showAction: false,
    moduleButtons: [
        {
            buttonType: 'nav',
            priority: 2.5,
            checkVisible: requirePageBuilderFlag,
            id: 'moduleManager-tab',
            location: 'Nav',
            href: '#admin/modules',
            text: 'module manager',
        },
    ],
    module: ModuleManager,
    ...ModuleManager,
});
